import { Ref, useEffect, useRef, useState } from 'react';
import {
  ClosedDropdownHandler,
  KEYEVENT,
  OpenDropdownHandler,
  UseAccessibleDropdownProps,
  UseAccessibleDropdownResult,
} from './useAccessibleDropdown.types';
import { DropdownItem } from '@base-app';

const isSafari = (): boolean => {
  const chromeInAgent = navigator.userAgent.includes('Chrome');
  const safariInAgent = navigator.userAgent.includes('Safari');

  return safariInAgent && !chromeInAgent;
};

const registerOpenDropdownHandlers = ({ items, activeIndex, setActiveIndex, select }: OpenDropdownHandler): any => {
  const keyDownCallback = (event: KeyboardEvent): void => {
    event.preventDefault();

    switch (event.key) {
      case KEYEVENT.UP:
      case KEYEVENT.ARROW_UP:
        setActiveIndex(activeIndex <= 0 ? items.length - 1 : activeIndex - 1);
        return;
      case KEYEVENT.DOWN:
      case KEYEVENT.ARROW_DOWN:
        setActiveIndex(activeIndex + 1 === items.length ? 0 : activeIndex + 1);
        return;
      case KEYEVENT.ENTER:
      case KEYEVENT.SPACE:
        select(items[activeIndex]);
        return;
      case KEYEVENT.ESC:
      case KEYEVENT.ESCAPE:
        select({ id: '', label: '' });
        return;
      case KEYEVENT.PAGE_UP:
      case KEYEVENT.HOME:
        setActiveIndex(0);
        return;
      case KEYEVENT.PAGE_DOWN:
      case KEYEVENT.END:
        setActiveIndex(items.length - 1);
    }
  };

  document.addEventListener('keydown', keyDownCallback);

  return (): void => {
    document.removeEventListener('keydown', keyDownCallback);
  };
};

const registerClosedDropdownHandlers = ({ setIsDropdownOpen }: ClosedDropdownHandler): any => {
  const keyDownCallback = (event: KeyboardEvent): void => {
    switch (event.key) {
      case KEYEVENT.UP:
      case KEYEVENT.ARROW_UP:
      case KEYEVENT.DOWN:
      case KEYEVENT.ARROW_DOWN:
      case KEYEVENT.SPACE:
      case KEYEVENT.ENTER:
        event.preventDefault();
        setIsDropdownOpen(true);
    }
  };
  document.addEventListener('keydown', keyDownCallback);

  return () => {
    document.removeEventListener('keydown', keyDownCallback);
  };
};

const useAccessibleDropdown = ({ items, value, onChange }: UseAccessibleDropdownProps): UseAccessibleDropdownResult => {
  const listRef: Ref<HTMLUListElement | any> = useRef();
  const [isDropdownOpen, setIsDropdownOpenInternal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocus, setIsFocus] = useState(false);

  const select = (value: DropdownItem): void => {
    onChange?.(value);
    setIsDropdownOpen(false);
  };

  const setIsDropdownOpen = (isOpen: boolean): void => {
    if (isOpen) {
      const selected = items.findIndex((item) => item.id === value);

      setActiveIndex(selected < 0 ? 0 : selected);

      if (listRef.current?.role !== 'listbox' && isSafari()) {
        requestAnimationFrame(() => {
          listRef.current?.focus();
        });
      }
    } else {
      if (listRef.current?.role !== 'listbox' && isSafari()) {
        requestAnimationFrame(() => {
          (listRef?.current?.previousElementSibling as HTMLElement)?.focus();
        });
      }
    }
    setIsDropdownOpenInternal(isOpen);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      return registerOpenDropdownHandlers({
        activeIndex,
        setActiveIndex,
        items,
        select,
      });
    } else {
      if (isFocus) {
        return registerClosedDropdownHandlers({
          setIsDropdownOpen,
        });
      }
    }
  }, [isDropdownOpen, activeIndex, isFocus]);

  return {
    activeIndex,
    isDropdownOpen,
    isFocus,
    listRef,
    select,
    setActiveIndex,
    setIsDropdownOpen,
    setIsFocus,
    registerClosedDropdownHandlers,
    registerOpenDropdownHandlers,
  };
};

export default useAccessibleDropdown;
